import { useCallback, useState, useMemo } from "react";
import { Button, Table, Form } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import DeviceLogApi from "./DeviceLogApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel } from "@fortawesome/free-solid-svg-icons";
import Header from "./Header";
import "./ConvertLog.css";

function ConvertLog() {
    const [file, setFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [logApi, setLogApi] = useState(null);
    const { t } = useTranslation();

    const valuableRecords = useMemo(() => logApi && logApi.valuableRecords(), [logApi]);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);

    const processFile = useCallback((file) => {
        if (!file) return;
        
        setFile(file);
        setLogApi(null);

        const reader = new FileReader();
        reader.onload = (e) => {
            const arrayBuffer = e.target.result;
            try {
                const api = new DeviceLogApi(arrayBuffer);
                setLogApi(api);
            } catch (error) {
                console.error("Error parsing log file:", error);
            }
        };
        reader.readAsArrayBuffer(file);
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            processFile(droppedFile);
        }
    }, [processFile]);

    const handleFileChange = useCallback((e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            processFile(selectedFile);
        }
    }, [processFile]);

    const handleConvert = useCallback(() => {
        if (file) {
            processFile(file);
        }
    }, [file, processFile]);

    const exportXlsx = useCallback(() => {
        if (!logApi) return;

        const excelBuffer = logApi.exportToXlsx({ pretty: true });

        // Create filename
        const now = new Date();
        const pad = n => n.toString().padStart(2, '0');
        const YYYY = now.getFullYear();
        const MM = pad(now.getMonth() + 1);
        const DD = pad(now.getDate());
        const HH = pad(now.getHours());
        const mm = pad(now.getMinutes());
        const ss = pad(now.getSeconds());
        const fileName = `log_${YYYY}-${MM}-${DD}_${HH}-${mm}-${ss}.xlsx`;

        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [logApi]);

    function getTimeText(record) {
        const sec = record.time2s * 2;
        const hours = Math.floor(sec / 3600);
        const minutes = Math.floor((sec - (hours * 3600)) / 60);
        const seconds = sec - (hours * 3600) - (minutes * 60);
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }

    function getModeText(record) {
        const modeText = `${DeviceLogApi.intCodeToCharCode(DeviceLogApi.MAIN_STATES, record.mainState)}/${DeviceLogApi.intCodeToCharCode(DeviceLogApi.CONTROL_MODES, record.controlMode)}/${DeviceLogApi.intCodeToCharCode(DeviceLogApi.HEATER_STATES, record.heaterState)}`;
        const chargeText = `${DeviceLogApi.intCodeToCharCode(DeviceLogApi.CHARGE_PLUGGED_STATES, record.chargePlugged)}/${DeviceLogApi.intCodeToCharCode(DeviceLogApi.CHARGE_END_STATES, record.chargeEnd)}`;
        return `${t('device.log.mode')}: ${modeText} ${t('device.log.charge')}: ${chargeText}`;
    }

    function getPowerText(record) {
        let items = [];
        if (record.voltage !== null) {
            items.push(record.voltage.toFixed(2) + t('V'));
        }
        if (record.current !== null) {
            items.push(record.current.toFixed(2) + t('A'));
        }
        return items.join(", ");
    }

    function getTemperatureText(record) {
        let items = [];
        if (record.mcuTemp !== null) {
            items.push(t('device.log.mcuTemp') + `=${record.mcuTemp}°C`);
        }
        if (record.sensTemp !== null) {
            items.push(t('device.log.sensTemp') + `=${record.sensTemp}°C`);
        }
        return items.join(", ");
    }

    return (
        <>
            <Helmet>
                <title>{t("convertLog.title") + " - KooApp"}</title>
            </Helmet>

            <Header />

            <div className="container my-4">
                <h2><Trans>convertLog.title</Trans></h2>
            
            <Form.Group className="mb-3">
                <Form.Label><Trans>convertLog.selectFile</Trans></Form.Label>
                <div
                    className={`drop-zone ${isDragging ? 'dragging' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    <div className="drop-zone-content">
                        <p className="mb-2">
                            <Trans>convertLog.dragDrop</Trans>
                        </p>
                        <Form.Control
                            type="file"
                            onChange={handleFileChange}
                            accept=".bin,application/octet-stream"
                            className="file-input"
                        />
                    </div>
                </div>
            </Form.Group>

            {file && (
                <div className="mb-3">
                    <strong><Trans>convertLog.selectedFile</Trans>:</strong> {file.name}
                </div>
            )}

            <Button
                variant="primary"
                onClick={handleConvert}
                disabled={!file}
                className="mb-4"
            >
                <Trans>convertLog.convert</Trans>
            </Button>

            {logApi && (
                <div className="mt-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h3><Trans>convertLog.result</Trans></h3>
                        {valuableRecords && valuableRecords.length > 0 && (
                            <Button
                                variant="success"
                                onClick={exportXlsx}
                            >
                                <FontAwesomeIcon icon={faFileExcel} /> <Trans>convertLog.export</Trans>
                            </Button>
                        )}
                    </div>

                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th><Trans>device.log.time</Trans></th>
                                <th><Trans>device.log.state</Trans></th>
                                <th><Trans>device.log.power</Trans></th>
                                <th><Trans>device.log.temperature</Trans></th>
                            </tr>
                        </thead>

                        <tbody>
                            {valuableRecords && valuableRecords.length > 0 ? (
                                valuableRecords.map((record, index) => (
                                    <tr key={index}>
                                        <td>{getTimeText(record)}</td>
                                        <td>{getModeText(record)}</td>
                                        <td>{getPowerText(record)}</td>
                                        <td>{getTemperatureText(record)}</td>
                                    </tr>
                                ))) : (
                                <tr>
                                    <td colSpan="4"><Trans>device.log.noRecords</Trans></td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            )}
            </div>
        </>
    );
}

export default ConvertLog;
